/**
 * GTM web + Meta Pixel loader.
 *
 * Conversion path (must not break):
 *   pushMetaEvent(meta_lead|meta_purchase|…) → dataLayer
 *     → GTM Pixel_Event tag → fbq() [browser]
 *     → GTM GA4 tag → sGTM / Stape CAPI [server]
 *
 * Official constraints this follows:
 * - Google: install the web container as early as possible so tags (including
 *   Meta PageView) can fire on first load.
 * - Meta: Pixel base code / GTM Pixel tag must run on load and leave PageView
 *   intact — that is what Ads Manager uses for landing page views.
 * - Meta CAPI + GTM SS: do NOT fire conversion events with a second in-app
 *   fbq(); keep Lead/Purchase on dataLayer → Pixel_Event + Stape with event_id.
 *
 * Strategy:
 * 1. Keep dataLayer + fbq stub available immediately so early pushes queue.
 * 2. Download gtm.js / fbevents.js on schedule (boot), not after a 15s wait.
 * 3. Leave fbAutoPageView false so GTM remains the PageView owner (no double
 *    PageView vs the web container Pixel tag).
 * 4. On any meta_* push, force-start the download if boot has not finished.
 */
const FB_SCRIPT_SRC = 'https://connect.facebook.net/en_US/fbevents.js';

export type MarketingTagsConfig = {
    gtmId?: string;
    fbPixelIds?: string[];
    fbAutoPageView?: boolean;
};

export type MarketingTagsScheduleOptions = {
    interactionDelayMs?: number;
    fallbackMs?: number;
};

type FbqStub = ((...args: any[]) => void) & {
    callMethod?: (...args: any[]) => void;
    queue: IArguments[];
    push: FbqStub;
    loaded: boolean;
    version: string;
};

declare global {
    interface Window {
        dataLayer: any[];
        fbq?: FbqStub;
        _fbq?: FbqStub;
    }
}

let config: MarketingTagsConfig = {};
let loadPromise: Promise<void> | null = null;
let scheduleCancel: (() => void) | null = null;
let scheduled = false;
let loadCompleted = false;
const loadedListeners = new Set<() => void>();
const bootstrappedGtmIds = new Set<string>();

function getStoredAdvancedMatchingData(): { em: string } | null {
    try {
        if (typeof localStorage === 'undefined') return null;
        const stored = JSON.parse(localStorage.getItem('funnel-v3-storage') || '{}');
        const email = stored?.state?.data?.email;
        if (email && typeof email === 'string') {
            return { em: email.toLowerCase().trim() };
        }
    } catch {
        /* ignore */
    }
    return null;
}

export function configureMarketingTags(partial: MarketingTagsConfig): void {
    config = {
        ...config,
        ...partial,
        fbPixelIds: partial.fbPixelIds ?? config.fbPixelIds,
    };
}

/** Always-available dataLayer so meta_* / GA4 pushes queue before gtm.js. */
export function ensureDataLayer(): void {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
}

/**
 * Official Meta Pixel stub — queues fbq() calls until fbevents.js loads.
 * Safe to call multiple times.
 */
export function ensureFbqStub(): void {
    if (typeof window === 'undefined') return;
    if (window.fbq) return;

    const stub: FbqStub = function (..._args: any[]) {
        // eslint-disable-next-line prefer-rest-params
        const args = arguments;
        if (stub.callMethod) {
            stub.callMethod.apply(stub, args as any);
        } else {
            stub.queue.push(args);
        }
    } as FbqStub;

    stub.push = stub;
    stub.loaded = true;
    stub.version = '2.0';
    stub.queue = [];
    window.fbq = stub;
    if (!window._fbq) window._fbq = stub;
}

function injectScript(src: string, attrs?: Record<string, string>): Promise<void> {
    return new Promise((resolve, reject) => {
        if (typeof document === 'undefined') {
            resolve();
            return;
        }
        if (document.querySelector(`script[src="${src}"]`)) {
            resolve();
            return;
        }
        const script = document.createElement('script');
        script.async = true;
        script.src = src;
        if (attrs) {
            Object.entries(attrs).forEach(([key, value]) => {
                script.setAttribute(key, value);
            });
        }
        let settled = false;
        const finish = (fn: () => void) => {
            if (settled) return;
            settled = true;
            fn();
        };
        script.onload = () => finish(() => {
            script.setAttribute('data-mc-loaded', 'true');
            resolve();
        });
        script.onerror = () => finish(() => {
            script.remove();
            reject(new Error(`Failed to load ${src}`));
        });
        const first = document.getElementsByTagName('script')[0];
        if (first?.parentNode) {
            first.parentNode.insertBefore(script, first);
        } else {
            document.head.appendChild(script);
        }
        // jsdom does not fetch external scripts — resolve immediately under Jest.
        if (typeof process !== 'undefined' && process.env.NODE_ENV === 'test') {
            finish(() => resolve());
        }
    });
}

function queueFacebookInit(): void {
    ensureFbqStub();
    const ids = (config.fbPixelIds || []).filter(Boolean);
    if (ids.length === 0 || !window.fbq) return;

    const storedUserData = getStoredAdvancedMatchingData();
    for (const id of ids) {
        window.fbq('set', 'autoConfig', false, id);
        if (storedUserData) {
            window.fbq('init', id, storedUserData);
        } else {
            window.fbq('init', id);
        }
    }
    if (config.fbAutoPageView) {
        window.fbq('track', 'PageView');
    }
}

function injectGtm(gtmId: string): Promise<void> {
    ensureDataLayer();
    // Mirror standard GTM bootstrap so the container sees gtm.js start.
    if (!bootstrappedGtmIds.has(gtmId)) {
        bootstrappedGtmIds.add(gtmId);
        window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
    }
    return injectScript(`https://www.googletagmanager.com/gtm.js?id=${gtmId}`, {
        'data-mc-marketing': 'gtm',
    }).then(() => {
        if (typeof document === 'undefined') return;
        if (document.querySelector(`noscript[data-mc-marketing="gtm"]`)) return;
        const noscript = document.createElement('noscript');
        noscript.setAttribute('data-mc-marketing', 'gtm');
        const iframe = document.createElement('iframe');
        iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
        iframe.height = '0';
        iframe.width = '0';
        iframe.style.display = 'none';
        iframe.style.visibility = 'hidden';
        noscript.appendChild(iframe);
        document.body.insertBefore(noscript, document.body.firstChild);
    });
}

function notifyLoaded(): void {
    loadCompleted = true;
    loadedListeners.forEach((listener) => {
        try {
            listener();
        } catch {
            /* non-blocking */
        }
    });
    loadedListeners.clear();
}

/**
 * Download GTM + Meta Pixel scripts (idempotent).
 * Queues fbq init before fbevents.js so Lead/Purchase via GTM still bind to pixels.
 */
export function loadMarketingTags(): Promise<void> {
    if (loadPromise) return loadPromise;
    const hasGtm = !!config.gtmId;
    const hasMetaPixel = (config.fbPixelIds || []).some(Boolean);

    // A child effect can request readiness before provider effects configure IDs.
    // Do not memoize that empty attempt; queued events will load once configured.
    if (!hasGtm && !hasMetaPixel) return Promise.resolve();

    loadPromise = (async () => {
        if (typeof window === 'undefined') return;

        ensureDataLayer();
        ensureFbqStub();
        queueFacebookInit();

        const tasks: Promise<void>[] = [];
        if (config.gtmId) {
            tasks.push(injectGtm(config.gtmId));
        }
        if (hasMetaPixel) {
            tasks.push(
                injectScript(FB_SCRIPT_SRC, { 'data-mc-marketing': 'fbq' }),
            );
        }

        await Promise.all(tasks);
        notifyLoaded();
    })().catch((error) => {
        loadPromise = null;
        loadCompleted = false;
        throw error;
    });

    return loadPromise;
}

/**
 * Start GTM + Pixel on boot. Options are accepted for call-site compatibility
 * but no longer delay attribution tags (that broke Meta landing page views).
 */
export function scheduleMarketingTags(
    _options: MarketingTagsScheduleOptions = {},
): () => void {
    if (typeof window === 'undefined') return () => undefined;
    if (loadPromise || scheduled) {
        return () => undefined;
    }

    scheduled = true;
    scheduleCancel = null;
    void loadMarketingTags().catch((error) => {
        console.error('[marketingTags] Attribution load failed', error);
    });

    // Providers share this global scheduler. Their StrictMode cleanup must not
    // cancel it for the other provider or prevent the second effect mount.
    return () => undefined;
}

/**
 * Force tags to start loading now (conversion-safe).
 * Used by pushMetaEvent so meta_lead / meta_purchase never depend on a late boot.
 */
export function ensureMarketingTagsReady(): Promise<void> {
    if (scheduleCancel) {
        scheduleCancel();
        scheduleCancel = null;
    }
    return loadMarketingTags();
}

/** Subscribe once tags finish loading (or fire immediately if already loaded). */
export function onMarketingTagsLoaded(listener: () => void): () => void {
    if (loadCompleted) {
        listener();
        return () => undefined;
    }
    loadedListeners.add(listener);
    return () => {
        loadedListeners.delete(listener);
    };
}

/** Test helper — reset module state between Jest cases. */
export function __resetMarketingTagsForTests(): void {
    config = {};
    loadPromise = null;
    scheduled = false;
    loadCompleted = false;
    loadedListeners.clear();
    bootstrappedGtmIds.clear();
    if (scheduleCancel) {
        scheduleCancel();
        scheduleCancel = null;
    }
    if (typeof window !== 'undefined') {
        delete (window as any).fbq;
        delete (window as any)._fbq;
        if (window.dataLayer) window.dataLayer.length = 0;
    }
}
